import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface Props {
  children?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
  }

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-3xl shadow-premium p-8 text-center border border-slate-100">
            <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="h-10 w-10 text-red-500" />
            </div>
            <h1 className="text-2xl font-black text-slate-900 mb-2">Something went wrong</h1>
            <p className="text-slate-500 mb-8 font-medium">
              We encountered an unexpected error. Don't worry, your data is safe.
            </p>
            
            <div className="bg-slate-50 rounded-2xl p-4 mb-6 text-left">
              <p className="text-xs font-mono text-slate-400 uppercase tracking-widest mb-2 font-bold">Error Details</p>
              <p className="text-sm font-mono text-red-600 break-all bg-white p-3 rounded-xl border border-red-50">
                {this.state.error?.message || 'Unknown technical error'}
              </p>
            </div>

            <div className="flex flex-col gap-3">
              <Button 
                onClick={() => window.location.reload()} 
                className="h-12 rounded-xl font-bold gap-2 shadow-lg shadow-primary/20"
              >
                <RefreshCw className="h-4 w-4" />
                Reload Application
              </Button>
              <Button 
                variant="outline" 
                onClick={() => window.location.href = '/'}
                className="h-12 rounded-xl font-bold gap-2 border-slate-200"
              >
                <Home className="h-4 w-4" />
                Return to Home
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
